<template>
  <v-card
    elevation="0"
    rounded="lg"
    class="stat-card"
    :class="{ 'stat-card--static': !card.action }"
  >
    <!-- brand rail: what keeps a row of eight tiles from reading as a spreadsheet -->
    <span class="card-rail"></span>

    <div class="stat-body">
      <div class="stat-head">
        <div class="icon-tile"><v-icon size="20">{{ iconName }}</v-icon></div>
        <span class="stat-label">{{ card.title }}</span>
      </div>

      <div class="stat-figure">
        <span class="value">{{ displayValue }}</span>
        <span class="value-rule"></span>
      </div>

      <span class="stat-hint" v-if="card.hint">{{ card.hint }}</span>

      <div class="stat-actions" v-if="card.action">
        <span class="action-link" @click="$emit('action', card)">
          {{ card.action }}
          <v-icon class="action-icon">mdi-arrow-right</v-icon>
        </span>
      </div>
    </div>
  </v-card>
</template>

<script>
/*
 * The one stat tile every dashboard uses. Each role's dashboard owns its own
 * data and its own routing, so this takes a card object and emits `action`
 * rather than navigating itself.
 *
 * card: { title, value, mdiIcon, action?, hint? }
 *
 * Every tile wears the same emerald as the sidebar — the glyph is what tells
 * one card from the next, not the colour.
 */
export default {
  name: "StatCard",
  props: {
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // the figure animates up to card.value; see the watcher below
      shownNumber: 0,
      rafId: null,
    };
  },
  computed: {
    iconName() {
      var c = this.card;
      if (c.mdiIcon) return c.mdiIcon;
      // the response dashboard calls its glyph `icon`; elsewhere `icon` is a
      // PNG require() path, which would render as a broken ligature
      if (typeof c.icon === "string" && c.icon.indexOf("mdi-") === 0) {
        return c.icon;
      }
      return "mdi-chart-box-outline";
    },
    numericValue() {
      var v = this.card.value;
      if (typeof v === "number" && isFinite(v)) return v;
      // counts arrive as strings from some endpoints; anything else (the
      // response dashboard's "12m 30s", "Awaiting", "—") is not a number
      if (typeof v === "string" && /^\d+$/.test(v.trim())) {
        return parseInt(v.trim(), 10);
      }
      return null;
    },
    displayValue() {
      if (this.numericValue === null) return this.card.value;
      return this.shownNumber.toLocaleString("en-IN");
    },
  },
  watch: {
    /* the tiles render at 0 and are filled in when the API answers, so count
       up to the new figure instead of letting it snap into place */
    numericValue: {
      immediate: true,
      handler(to, from) {
        if (to === null) return;
        this.countTo(to, typeof from === "number" ? from : 0);
      },
    },
  },
  beforeDestroy() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
  },
  methods: {
    countTo(to, from) {
      if (this.rafId) cancelAnimationFrame(this.rafId);

      var reduced =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      // nothing to watch on a jump of a few, and no motion if they asked for none
      if (reduced || Math.abs(to - from) < 5) {
        this.shownNumber = to;
        return;
      }

      var self = this;
      var duration = 900;
      var start = null;
      var step = function (now) {
        if (start === null) start = now;
        var p = Math.min((now - start) / duration, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        self.shownNumber = Math.round(from + (to - from) * eased);
        if (p < 1) {
          self.rafId = requestAnimationFrame(step);
        } else {
          self.rafId = null;
        }
      };
      this.rafId = requestAnimationFrame(step);
    },
  },
};
</script>

<style scoped>
/* ---------- card shell ----------
   White and flat rather than tinted: the number carries the emphasis, and the
   emerald is concentrated in the rail, the icon tile, the corner wash, the rule
   under the value and the action. Change the variables at the bottom to
   restyle every dashboard's cards at once. */
.stat-card {
  position: relative;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  border-radius: 16px !important;
  border: 1px solid var(--line) !important;
  background: #ffffff !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05) !important;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
    box-shadow 0.3s ease, border-color 0.3s ease !important;
}
/* soft wash bleeding in from the top-right corner — the thing that stops eight
   white rectangles from reading as a table */
.stat-card::before {
  content: "";
  position: absolute;
  top: -58px;
  right: -58px;
  width: 176px;
  height: 176px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--wash) 0%, rgba(255, 255, 255, 0) 70%);
  transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease;
  pointer-events: none;
}
.stat-card:hover::before {
  transform: scale(1.18);
}
.stat-card:hover {
  transform: translateY(-4px);
  border-color: var(--line-hover) !important;
  box-shadow: 0 18px 34px -18px var(--lift-shadow) !important;
}
.stat-card:active {
  transform: translateY(-1px);
}
/* nothing to click on a metric-only tile, so don't promise otherwise */
.stat-card--static {
  cursor: default;
  /* without an action pill these tiles come out shorter than the ones that have
     it, so give them a floor and centre the content in the room */
  min-height: 176px;
}
.stat-card--static .stat-body {
  justify-content: center;
}

/* brand rail across the top edge, always on */
.card-rail {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--accent) 0%, var(--accent-deep) 100%);
  opacity: 0.85;
  transform-origin: left center;
  transition: opacity 0.3s ease;
  pointer-events: none;
}
.stat-card:hover .card-rail {
  opacity: 1;
}

.stat-body {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 22px 20px 18px 20px;
}

/* ---------- icon + label sit on one line ---------- */
.stat-head {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}
.icon-tile {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* starts one step below --accent on purpose: at the brightest emerald the
     white glyph only reaches 2.5:1, and this is the one place white sits on
     the brand colour */
  background: linear-gradient(
    135deg,
    var(--accent-tile) 0%,
    var(--accent-deep) 100%
  );
  box-shadow: 0 6px 16px -6px var(--accent-shadow);
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1),
    box-shadow 0.35s ease;
}
.icon-tile .v-icon {
  color: #ffffff !important;
}
.stat-card:hover .icon-tile {
  transform: translateY(-1px) scale(1.07) rotate(-4deg);
  box-shadow: 0 10px 20px -6px var(--accent-shadow);
}
.stat-label {
  min-width: 0;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: var(--muted) !important;
  line-height: 1.3;
}

/* ---------- the figure ---------- */
.stat-figure {
  margin-top: 18px;
}
.stat-card .value {
  display: block;
  font-family: poppinsbold, poppinssemibold, sans-serif;
  font-size: 34px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -1.2px;
  color: var(--ink) !important;
  /* proportional figures on purpose: tabular digits make a number this large
     look gappy, and nothing here has to line up in a column */
  /* the response dashboard puts strings like "12m 30s" here, not just counts */
  word-break: break-word;
}
.value-rule {
  display: block;
  width: 28px;
  height: 3px;
  margin-top: 10px;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--accent) 0%, var(--accent-deep) 100%);
  transition: width 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.stat-card:hover .value-rule {
  width: 46px;
}
/* optional sub-label; only the response dashboard and the alert view supply one */
.stat-hint {
  margin-top: 10px;
  font-family: poppinsregular, sans-serif;
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--hint);
}

/* ---------- action ---------- */
.stat-actions {
  margin-top: auto;
  padding-top: 18px;
}
.stat-card .action-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 15px;
  border: 1px solid var(--line-hover);
  border-radius: 10px;
  background: var(--tint);
  color: var(--accent-deep) !important;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  cursor: pointer;
  transition: background 0.22s ease, border-color 0.22s ease, color 0.22s ease,
    box-shadow 0.22s ease;
}
.stat-card .action-icon {
  font-size: 15px !important;
  color: inherit !important;
  transition: transform 0.22s ease;
}
/* filled state uses the deeper pair, not --accent: a white label needs 4.5:1,
   which the brightest emerald does not give */
.stat-card .action-link:hover {
  background: linear-gradient(
    135deg,
    var(--accent-mid) 0%,
    var(--accent-deep) 100%
  );
  border-color: transparent;
  color: #ffffff !important;
  box-shadow: 0 8px 16px -8px var(--accent-shadow);
}
.stat-card .action-link:hover .action-icon {
  transform: translateX(3px);
}

/* ---------- palette ----------
   The sidebar's emerald, one theme for every card. --ink is deliberately
   near-black: the number is the thing being read, and a green numeral on a
   light card never clears 4.5:1. Green lives only in the rail, the icon tile,
   the corner wash, the watermark, the rule under the figure and the action.
   Change these values to restyle every dashboard's cards at once. */
.stat-card {
  --accent: #10b981; /* the sidebar emerald — decorative surfaces only */
  --accent-tile: #0ea97a; /* icon-tile gradient start; white glyph clears 3:1 */
  --accent-mid: #059669; /* filled-pill gradient start; white label clears 4.5:1 */
  --accent-deep: #047857; /* gradient end, and the action label on --tint (5.2:1) */
  --accent-shadow: rgba(5, 150, 105, 0.45);
  --wash: rgba(16, 185, 129, 0.1);
  --tint: #ecfdf5;
  --ink: #0f1f17;
  --muted: #64748b;
  --hint: #94a3b8;
  --line: #e8edf2;
  --line-hover: #a7f3d0;
  --lift-shadow: rgba(4, 120, 87, 0.28);
}

@media (prefers-reduced-motion: reduce) {
  .stat-card,
  .stat-card::before,
  .stat-card .card-rail,
  .stat-card .icon-tile,
  .stat-card .value-rule,
  .stat-card .action-link,
  .stat-card .action-icon {
    transition: none !important;
  }
  .stat-card:hover,
  .stat-card:hover::before,
  .stat-card:hover .icon-tile {
    transform: none !important;
  }
  .stat-card:hover .value-rule {
    width: 28px;
  }
}
</style>
